import { useState } from "react";
import BRAND from "@/brand/brand";

const { revenue, radar } = BRAND.colors;

// Radar-sweep glyph (brand colours). Used standalone and inside the wordmark fallback.
export function RadarMark({ className = "w-7 h-7" }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden="true" fill="none">
      <defs>
        <linearGradient id="rr-ring" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={radar[0]} />
          <stop offset="1" stopColor={radar[1]} />
        </linearGradient>
        <radialGradient id="rr-sweep" cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor={radar[0]} stopOpacity="0.85" />
          <stop offset="1" stopColor={radar[0]} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="16" cy="16" r="13" stroke="url(#rr-ring)" strokeWidth="2" opacity="0.9" />
      <circle cx="16" cy="16" r="8" stroke="url(#rr-ring)" strokeWidth="1.4" opacity="0.5" />
      <path d="M16 16 L16 3 A13 13 0 0 1 27 11 Z" fill="url(#rr-sweep)" />
      <circle cx="16" cy="16" r="2" fill={radar[1]} />
      <circle cx="22.5" cy="9.5" r="1.7" fill={revenue[1]} />
    </svg>
  );
}

// Gradient wordmark: "Revenue" (orange) + "Radar" (cyan/blue).
export function Wordmark({ className = "text-[15px]" }) {
  const grad = (c) => ({
    backgroundImage: `linear-gradient(90deg, ${c[0]}, ${c[1]})`,
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    color: "transparent",
  });
  return (
    <span className={`font-heading font-extrabold tracking-tight leading-none whitespace-nowrap ${className}`}>
      <span style={grad(revenue)}>Revenue</span>
      <span style={grad(radar)}>&nbsp;Radar</span>
    </span>
  );
}

// variant: "wordmark" (mark + text) | "mark" (glyph only) | "hero" (large lockup)
export default function BrandLogo({
  variant = "wordmark",
  className = "",
  markClass,
  textClass,
  imgClass,
  showText = true,
  alt,
}) {
  const [imgOk, setImgOk] = useState(true);
  const src = variant === "hero" ? BRAND.heroLogo : BRAND.wordmark;

  if (variant === "mark") {
    return <RadarMark className={markClass || className || "w-8 h-8"} />;
  }

  if (variant === "hero") {
    return (
      <div className={className} data-testid="brand-hero-logo">
        {imgOk && src ? (
          <img
            src={src}
            onError={() => setImgOk(false)}
            alt={alt || `${BRAND.name} — ${BRAND.subtitle}`}
            className="w-full h-auto object-contain select-none"
            draggable="false"
          />
        ) : (
          <div className="flex flex-col items-center gap-5 select-none">
            <RadarMark className="w-24 h-24" />
            <Wordmark className="text-5xl sm:text-6xl" />
          </div>
        )}
      </div>
    );
  }

  // wordmark
  return (
    <span className={`inline-flex items-center gap-2 ${className}`} data-testid="brand-wordmark">
      {imgOk && src ? (
        <img
          src={src}
          onError={() => setImgOk(false)}
          alt={alt || BRAND.name}
          className={imgClass || "h-8 w-auto object-contain select-none"}
          draggable="false"
        />
      ) : (
        <>
          <RadarMark className={markClass || "w-7 h-7 shrink-0"} />
          {showText && <Wordmark className={textClass || "text-[15px]"} />}
        </>
      )}
    </span>
  );
}
